import { NoteRepository } from '../data/db'
import type { AssetTransport } from './assetTransport'
import type { ImageProcessor } from './imageProcessor'
import type { NoteAsset } from './assetTypes'
import { APP_SETTINGS } from '../config/appSettings'

type AssetQueueListener = (assets: NoteAsset[]) => void

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message.slice(0, 240) : 'Image job failed.'
}

function retryTime(attempts: number, now = Date.now()): number {
  return now + Math.min(
    APP_SETTINGS.sync.retryMaxMs,
    APP_SETTINGS.sync.retryBaseMs * 2 ** Math.min(attempts, APP_SETTINGS.sync.retryExponentCap),
  )
}

export class ImageQueue {
  private running: Promise<void> | null = null
  private requested = false
  private latestUserId: string | null = null
  private readonly listeners = new Set<AssetQueueListener>()

  constructor(
    private readonly repository: NoteRepository,
    private readonly processor: ImageProcessor,
    private readonly transport: AssetTransport | null,
  ) {}

  subscribe(listener: AssetQueueListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  wake(userId: string | null): Promise<void> {
    this.latestUserId = userId
    this.requested = true
    if (this.running) return this.running
    this.running = this.drain(this.latestUserId).finally(() => {
      this.running = null
      if (this.requested) void this.wake(this.latestUserId)
    })
    return this.running
  }

  private async drain(userId: string | null): Promise<void> {
    this.requested = false
    await this.repository.recoverInterruptedAssetJobs()
    const processingCandidates = await this.repository.listAssetsByStates(['local', 'error'])
    for (const asset of processingCandidates) {
      if (asset.state === 'error' && asset.errorStage !== 'processing') continue
      if (asset.nextAttemptAt > Date.now()) continue
      await this.process(asset)
    }

    if (!userId || !this.transport || !navigator.onLine) return
    const uploadCandidates = await this.repository.listAssetsByStates(['ready_to_upload', 'error'])
    const ready: NoteAsset[] = []
    for (const asset of uploadCandidates) {
      if (
        asset.nextAttemptAt > Date.now()
        || (asset.state === 'error' && asset.errorStage !== 'upload')
      ) continue
      const parent = await this.repository.getNote(asset.noteId)
      if (
        parent?.localSyncState !== 'synced'
        || !parent.assetIds.includes(asset.id)
      ) continue
      ready.push(asset)
    }
    const concurrency = APP_SETTINGS.images.maxConcurrentUploads
    for (let index = 0; index < ready.length; index += concurrency) {
      await Promise.all(ready.slice(index, index + concurrency).map((asset) => this.upload(asset, userId)))
    }

    const deleted = (await this.repository.listAssetsByStates(['deleted']))
      .filter((asset) => asset.nextAttemptAt <= Date.now())
    for (const asset of deleted) await this.remove(asset)
  }

  private async process(asset: NoteAsset): Promise<void> {
    if (!asset.sourceBlob) return
    const processing = { ...asset, state: 'processing' as const, modifiedAt: Date.now() }
    await this.repository.putAsset(processing)
    this.emit([processing])
    try {
      const result = await this.processor.process(
        asset.id,
        asset.sourceBlob,
        asset.originalMimeType,
      )
      const ready: NoteAsset = {
        ...processing,
        processedMasterBlob: result.master,
        thumbnailBlob: result.thumbnail,
        width: result.width,
        height: result.height,
        masterMimeType: result.masterMimeType,
        thumbnailMimeType: result.thumbnailMimeType,
        processedSize: result.processedSize,
        state: 'ready_to_upload',
        errorStage: null,
        attempts: 0,
        nextAttemptAt: Date.now(),
        lastError: null,
        modifiedAt: Date.now(),
      }
      await this.repository.putAsset(ready)
      this.emit([ready])
    } catch (error) {
      const attempts = processing.attempts + 1
      const failed: NoteAsset = {
        ...processing,
        state: 'error',
        errorStage: 'processing',
        attempts,
        nextAttemptAt: retryTime(attempts),
        lastError: errorMessage(error),
        modifiedAt: Date.now(),
      }
      await this.repository.putAsset(failed)
      this.emit([failed])
    }
  }

  private async upload(asset: NoteAsset, userId: string): Promise<void> {
    if (!this.transport) return
    const uploading = { ...asset, state: 'uploading' as const, modifiedAt: Date.now() }
    await this.repository.putAsset(uploading)
    this.emit([uploading])
    try {
      const remote = await this.transport.upload(uploading, userId)
      const synced = await this.repository.applyRemoteAsset(remote)
      // The processed master and thumbnail remain available offline. Discard only
      // the source after the remote master and metadata are both durable.
      const compact: NoteAsset = {
        ...synced,
        sourceBlob: null,
      }
      await this.repository.putAsset(compact)
      this.emit([compact])
    } catch (error) {
      const attempts = uploading.attempts + 1
      const failed: NoteAsset = {
        ...uploading,
        state: 'error',
        errorStage: 'upload',
        attempts,
        nextAttemptAt: retryTime(attempts),
        lastError: errorMessage(error),
        modifiedAt: Date.now(),
      }
      await this.repository.putAsset(failed)
      this.emit([failed])
    }
  }

  private async remove(asset: NoteAsset): Promise<void> {
    if (!this.transport) return
    try {
      await this.transport.remove(asset)
      await this.repository.deleteAsset(asset.id)
    } catch (error) {
      const attempts = asset.attempts + 1
      const failed: NoteAsset = {
        ...asset,
        errorStage: 'delete',
        attempts,
        nextAttemptAt: retryTime(attempts),
        lastError: errorMessage(error),
        modifiedAt: Date.now(),
      }
      await this.repository.putAsset(failed)
      this.emit([failed])
    }
  }

  private emit(assets: NoteAsset[]): void {
    for (const listener of this.listeners) listener(assets)
  }
}
